/**
 * Expanded detail for a `rate_cards_ingested` feed root. Renders one section per
 * carrier the run touched (an agent card lists every carrier it sent; a direct
 * carrier card has a single section), each with new-vs-updated lane stats and a
 * table of the most significant price moves. Clicking a lane loads its weekly
 * FRT history (sparkline) plus the involved cards' policy snapshots on demand.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ChevronDown,
  Loader2,
  Minus,
  TrendingDown,
  TrendingUp,
} from "lucide-react";

import { SensitiveMoney } from "@/components/common/Sensitive";
import { useUserPreferences } from "@/context/UserPreferencesContext";
import { usePortNames } from "@/hooks/usePortNames";
import { extractLaneLocodes, formatLaneKey } from "@/lib/formatPort";
import { cn } from "@/lib/utils";
import { api } from "@/services/api";
import { LineChart } from "@/components/charts/LineChart";
import type { LineChartSpec } from "@/components/charts/types";
import type {
  CardPolicySnapshot,
  LaneFrtHistoryPoint,
  RateCarrierSummary,
  RateIngestPayload,
  RateLaneMove,
} from "@/types/feed";
import {
  bookingPolicyChips,
  formatValidityRanges,
  isRateIngestCarrierPayload,
  isRateIngestPayload,
  metaFromRateIngestPayload,
  overweightDisplaysFromMeta,
  type OverweightPolicyDisplay,
  type RateIngestDisplayMeta,
} from "./rateIngestDisplay";
import { useRateIngestMeta } from "./useRateIngestMeta";

export { isRateIngestCarrierPayload, isRateIngestPayload };

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

function formatUsd(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return usd.format(value);
}

function StatChips({
  newLanes,
  updatedLanes,
  newValidity,
}: {
  newLanes: number;
  updatedLanes: number;
  newValidity: number;
}) {
  if (newLanes <= 0 && updatedLanes <= 0 && newValidity <= 0) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {newLanes > 0 && (
        <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-300">
          +{newLanes} new
        </span>
      )}
      {updatedLanes > 0 && (
        <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-300">
          {updatedLanes} updated
        </span>
      )}
      {newValidity > 0 && (
        <span className="rounded-full bg-violet-500/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-violet-600 dark:text-violet-300">
          {newValidity} new validity
        </span>
      )}
    </span>
  );
}

function OverweightPolicyCard({ policy }: { policy: OverweightPolicyDisplay }) {
  return (
    <div className="feed-rate-policy">
      <p className="feed-rate-policy__label">Overweight</p>
      <p className="feed-rate-policy__scope">
        {policy.route}
        {policy.equipment ? ` · ${policy.equipment}` : ""}
      </p>
      {policy.steps.length > 0 && (
        <dl className="feed-rate-policy__tiers">
          {policy.steps.map((step) => (
            <div key={`${step.range}-${step.amount}`} className="feed-rate-policy__tier">
              <dt>{step.range}</dt>
              <dd>
                <SensitiveMoney formatted={step.amount} />
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

function RateIngestPolicies({ meta }: { meta: RateIngestDisplayMeta }) {
  const booking = bookingPolicyChips(meta.bookingSummary);
  const overweight = overweightDisplaysFromMeta(meta);
  if (booking.length === 0 && overweight.length === 0) return null;
  return (
    <div className="feed-rate-policies">
      {booking.length > 0 && (
        <div className="feed-rate-policy">
          <p className="feed-rate-policy__label">Booking</p>
          <div className="feed-rate-policy__chips">
            {booking.map((chip) => (
              <span key={chip}>{chip}</span>
            ))}
          </div>
        </div>
      )}
      {overweight.map((policy) => (
        <OverweightPolicyCard
          key={`${policy.route}-${policy.equipment ?? ""}`}
          policy={policy}
        />
      ))}
    </div>
  );
}

/** FRT delta pill (prev → new) with a directional tone. */
function DeltaPill({ move }: { move: RateLaneMove }) {
  const { frt_usd: frt, prev_frt_usd: prev } = move;
  if (frt === undefined) return <span className="text-muted-foreground">—</span>;
  if (prev === undefined || move.change_kind !== "updated") {
    return <span className="tabular-nums">{formatUsd(frt)}</span>;
  }
  const delta = frt - prev;
  const tone =
    delta < 0
      ? "text-emerald-600 dark:text-emerald-300"
      : delta > 0
        ? "text-rose-600 dark:text-rose-300"
        : "text-muted-foreground";
  const Icon = delta < 0 ? TrendingDown : delta > 0 ? TrendingUp : Minus;
  return (
    <span className={cn("inline-flex items-center gap-1 tabular-nums", tone)}>
      <span className="text-muted-foreground line-through">{formatUsd(prev)}</span>
      <ArrowUpRight className="size-2.5 rotate-45 text-muted-foreground" aria-hidden />
      {formatUsd(frt)}
      <Icon className="size-2.5" aria-hidden />
      <span className="text-[10px]">
        {delta > 0 ? "+" : ""}
        {formatUsd(delta)}
      </span>
    </span>
  );
}

/** Lazy-loaded weekly FRT sparkline + policy snapshots for one lane. */
function LaneHistory({
  eventId,
  carrier,
  move,
  cardIds,
}: {
  eventId: string;
  carrier: string;
  move: RateLaneMove;
  cardIds: string[];
}) {
  const [points, setPoints] = useState<LaneFrtHistoryPoint[] | null>(null);
  const [policies, setPolicies] = useState<CardPolicySnapshot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = () => {
    if (loaded || loading) return;
    setLoading(true);
    setError(null);
    api
      .getFeedRateIngestLaneHistory(eventId, {
        carrier,
        origin_port: move.origin_port ?? move.lane_key.split("-")[0] ?? "",
        destination_port:
          move.destination_port ?? move.lane_key.split("-")[1] ?? "",
        equipment_size: move.equipment_size,
        equipment_type: move.equipment_type,
        contract_ref: move.contract_ref,
        card_ids: cardIds.join(","),
        limit: 24,
      })
      .then((res) => {
        setPoints(res.points);
        setPolicies(res.policies);
        setLoaded(true);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Failed to load history");
      })
      .finally(() => setLoading(false));
  };

  // Kick off on first render of this expanded row.
  if (!loaded && !loading && !error) load();

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-2 py-2 text-[11px] text-muted-foreground">
        <Loader2 className="size-3 animate-spin" />
        Loading rate history…
      </div>
    );
  }
  if (error) {
    return <p className="px-2 py-2 text-[11px] text-rose-500">{error}</p>;
  }
  if (!points || points.length === 0) {
    return (
      <p className="px-2 py-2 text-[11px] text-muted-foreground">
        No prior versions recorded for this lane.
      </p>
    );
  }

  // Chronological order for the sparkline (API returns newest-first).
  const chrono = [...points].reverse();
  const spec: LineChartSpec = {
    type: "line",
    yTitle: "FRT (USD)",
    series: [
      {
        title: `${carrier} FRT`,
        valueFormat: "usd",
        data: chrono.map((p, i) => ({
          x: p.iso_week ?? p.effective_from ?? `v${p.version}`,
          y: p.frt,
          key: String(i),
        })),
      },
    ],
  };

  return (
    <div className="mt-1 flex flex-col gap-2 rounded-lg bg-background/40 p-2">
      <div className="h-32 w-full">
        <LineChart spec={spec} variant="compact" />
      </div>
      <div className="flex flex-col gap-0.5">
        {points.slice(0, 6).map((p) => (
          <div
            key={`${p.rate_card_id}-${p.version}`}
            className="flex items-center justify-between gap-2 text-[10px]"
          >
            <span className="text-muted-foreground">
              {p.iso_week ?? p.effective_from ?? `v${p.version}`}
              {p.is_current && (
                <span className="ml-1 rounded-full bg-emerald-500/15 px-1 py-px text-[8px] font-semibold uppercase text-emerald-600 dark:text-emerald-300">
                  current
                </span>
              )}
            </span>
            <span className="tabular-nums">
              {formatUsd(p.frt)}
              <Link
                to={`/rates/${encodeURIComponent(p.rate_card_id)}`}
                className="ml-1.5 inline-flex items-center gap-0.5 text-primary hover:underline"
              >
                v{p.version}
                <ArrowUpRight className="size-2" />
              </Link>
            </span>
          </div>
        ))}
      </div>
      {policies.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-1">
          {policies.map((pol) => (
            <Link
              key={pol.rate_card_id}
              to={`/rates/${encodeURIComponent(pol.rate_card_id)}`}
              className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary transition-colors hover:bg-primary/20"
            >
              {pol.contract_ref ?? pol.carrier} policies
              <ArrowUpRight className="size-2.5" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/** One carrier's collapsible section: stats + top-move table. */
function CarrierSection({
  eventId,
  carrier,
  defaultOpen,
  showLocodes,
  portNames,
  showPolicies,
}: {
  eventId: string;
  carrier: RateCarrierSummary;
  defaultOpen: boolean;
  showLocodes: boolean;
  portNames: ReadonlyMap<string, string>;
  showPolicies: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [activeLane, setActiveLane] = useState<string | null>(null);
  const carrierMeta = metaFromRateIngestPayload({
    kind: "rate_ingest_carrier",
    carrier: carrier.carrier,
    rate_card_ids: carrier.rate_card_ids,
    contract_refs: carrier.contract_refs,
    validity_ranges: carrier.validity_ranges,
    policies: carrier.policies,
    lines: carrier.lines,
    new_lanes: carrier.new_lanes,
    updated_lanes: carrier.updated_lanes,
    new_validity_buckets: carrier.new_validity_buckets,
    median_frt_delta_usd: carrier.median_frt_delta_usd,
    top_moves: carrier.top_moves,
  });
  const contract = carrier.contract_refs.find((ref) => ref.trim());
  const validity = formatValidityRanges(carrierMeta.validityRanges);

  return (
    <div className="rounded-lg bg-background/40">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-2.5 py-2 text-left"
      >
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate text-xs font-semibold">
              {carrier.carrier}
              {contract ? ` ${contract}` : ""}
            </span>
            <span className="text-[10px] text-muted-foreground">
              {carrier.lines} line{carrier.lines === 1 ? "" : "s"}
            </span>
          </span>
          {validity && (
            <span className="truncate text-[10px] text-muted-foreground">
              {validity}
            </span>
          )}
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          <StatChips
            newLanes={carrier.new_lanes}
            updatedLanes={carrier.updated_lanes}
            newValidity={carrier.new_validity_buckets}
          />
          <ChevronDown
            className={cn(
              "size-3.5 text-muted-foreground transition-transform",
              open && "rotate-180",
            )}
          />
        </span>
      </button>

      {open && showPolicies && (
        <div className="px-2.5 pb-2">
          <RateIngestPolicies meta={carrierMeta} />
        </div>
      )}

      {open && carrier.top_moves.length > 0 && (
        <div className="flex flex-col gap-0.5 px-2.5 pb-2">
          {carrier.median_frt_delta_usd !== undefined &&
            carrier.updated_lanes > 0 && (
              <p className="pb-1 text-[10px] text-muted-foreground">
                Median change {formatUsd(carrier.median_frt_delta_usd)} across{" "}
                {carrier.updated_lanes} updated lane
                {carrier.updated_lanes === 1 ? "" : "s"}
              </p>
            )}
          {carrier.top_moves.map((move, i) => {
            const laneId = `${move.lane_key}-${move.equipment_size ?? ""}-${move.equipment_type ?? ""}-${i}`;
            const isActive = activeLane === laneId;
            return (
              <div key={laneId} className="rounded-md">
                <button
                  type="button"
                  onClick={() =>
                    setActiveLane((cur) => (cur === laneId ? null : laneId))
                  }
                  className="flex w-full items-center justify-between gap-2 rounded-md px-1.5 py-1 text-left hover:bg-background/60"
                >
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate text-[11px] font-medium">
                      {formatLaneKey(move.lane_key, showLocodes, portNames)}
                    </span>
                    {move.equipment_size && (
                      <span className="rounded bg-white/5 px-1 py-px text-[9px] text-muted-foreground">
                        {move.equipment_size}
                        {move.equipment_type ? ` ${move.equipment_type}` : ""}
                      </span>
                    )}
                  </span>
                  <span className="flex shrink-0 items-center gap-1.5 text-[11px]">
                    <DeltaPill move={move} />
                    <ChevronDown
                      className={cn(
                        "size-3 text-muted-foreground transition-transform",
                        isActive && "rotate-180",
                      )}
                    />
                  </span>
                </button>
                {isActive && (
                  <LaneHistory
                    eventId={eventId}
                    carrier={carrier.carrier}
                    move={move}
                    cardIds={carrier.rate_card_ids}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function RateCardsIngestedFeedDetail({
  eventId,
  payload,
}: {
  eventId: string;
  payload: RateIngestPayload;
}) {
  const { preferences } = useUserPreferences();
  const meta = useRateIngestMeta(eventId, payload);
  const carriers = payload.carriers ?? [];
  const isAgent = payload.group?.kind === "agent";
  const laneLocodes = useMemo(
    () =>
      extractLaneLocodes(
        ...carriers.flatMap((carrier) =>
          carrier.top_moves.flatMap((move) => [
            move.lane_key,
            move.origin_port,
            move.destination_port,
          ]),
        ),
      ),
    [carriers],
  );
  const portNames = usePortNames(laneLocodes);
  const showTopPolicies = !isAgent && meta != null;

  return (
    <div className="feed-row-detail-body">
      <p className="feed-row-detail-body__heading">
        {isAgent
          ? `${carriers.length} carrier${carriers.length === 1 ? "" : "s"}`
          : "Rate update"}
        {payload.totals.lines > 0
          ? ` · ${payload.totals.lines} line${payload.totals.lines === 1 ? "" : "s"}`
          : ""}
      </p>
      {showTopPolicies && <RateIngestPolicies meta={meta} />}
      {carriers.map((carrier) => (
        <CarrierSection
          key={carrier.carrier}
          eventId={eventId}
          carrier={{
            ...carrier,
            validity_ranges: carrier.validity_ranges ?? meta?.validityRanges,
            policies:
              carrier.policies ??
              (meta
                ? {
                    booking: meta.bookingSummary,
                    overweight: meta.overweightSummaries,
                  }
                : carrier.policies),
          }}
          defaultOpen={carriers.length === 1}
          showLocodes={preferences.showLocodes}
          portNames={portNames}
          showPolicies={isAgent}
        />
      ))}
    </div>
  );
}
